import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdbService } from "../core/adb/AdbService";
import { FastbootService } from "../core/fastboot/FastbootService";
import { AmlogicSessionManager } from "../core/amlogic/multi/AmlogicSessionManager";
import type { DeviceInfo } from "../core/types";
import { userMessage } from "../core/errors";
import { operationLogger } from "../core/operations/OperationLogger";
import { BrowserCompatibility } from "../components/BrowserCompatibility";
import { DeviceConnectionCard } from "../components/DeviceConnectionCard";
import { OperationLog } from "../components/OperationLog";
import { PasswordGate } from "../components/PasswordGate";
import { AdbPage } from "../features/adb/AdbPage";
import { FastbootPage } from "../features/fastboot/FastbootPage";
import { AmlogicPage } from "../features/amlogic/AmlogicPage";
import { WindowsDriverGuidePage } from "../features/help/WindowsDriverGuidePage";
import { matchesProtocolDevice } from "../core/usb/UsbDeviceMatcher";
import { usePresence } from "../core/presence/PresenceClient";
import "../styles/app.css";
import "../styles/amlogic.css";
import "../styles/fastboot.css";

type Mode = "adb" | "fastboot" | "amlogic-v3" | "amlogic-v2";

function isAmlogicMode(mode: Mode): mode is "amlogic-v3" | "amlogic-v2" {
  return mode === "amlogic-v3" || mode === "amlogic-v2";
}

function sameUsbIdentity(left: USBDevice, right: USBDevice): boolean {
  if (left === right) return true;
  if (left.vendorId !== right.vendorId || left.productId !== right.productId) return false;
  if (left.serialNumber || right.serialNumber) return left.serialNumber === right.serialNumber;
  return left.manufacturerName === right.manufacturerName && left.productName === right.productName;
}

export function App() {
  const [route, setRoute] = useState<"workbench" | "driver-windows">(() => location.pathname.replace(/\/+$/u, "") === "/driver-windows" ? "driver-windows" : "workbench");

  useEffect(() => {
    const syncRoute = () => setRoute(location.pathname.replace(/\/+$/u, "") === "/driver-windows" ? "driver-windows" : "workbench");
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, []);

  const navigate = (next: "workbench" | "driver-windows") => {
    history.pushState({}, "", next === "driver-windows" ? "/driver-windows" : "/");
    setRoute(next);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  useEffect(() => {
    document.title = route === "driver-windows" ? "Hướng dẫn driver Windows · USB Device Workbench" : "USB Device Workbench";
  }, [route]);

  if (route === "driver-windows") return <WindowsDriverGuidePage onBack={() => navigate("workbench")} />;
  return <PasswordGate>{({ logout }) => <WorkbenchApp onLogout={logout} onOpenDriverGuide={() => navigate("driver-windows")} />}</PasswordGate>;
}

function WorkbenchApp({ onLogout, onOpenDriverGuide }: { onLogout: () => Promise<void>; onOpenDriverGuide: () => void }) {
  const services = useMemo(() => ({ adb: new AdbService(), fastboot: new FastbootService() }), []);
  const amlogicManager = useMemo(() => new AmlogicSessionManager(), []);
  const [mode, setMode] = useState<Mode>("amlogic-v3");
  const [connectedMode, setConnectedMode] = useState<"adb" | "fastboot">();
  const [info, setInfo] = useState<DeviceInfo>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const autoConnectAttempts = useRef(new Set<USBDevice>());
  const autoConnectInFlight = useRef(false);
  const [amlogicFlashing, setAmlogicFlashing] = useState(false);
  const presence = usePresence(amlogicFlashing);

  useEffect(() => {
    const syncFlashing = () => {
      setAmlogicFlashing(amlogicManager.getSnapshot().some((session) => (
        session.status === "queued"
        || session.status === "running"
        || session.status === "waiting-reenumeration"
        || session.status === "reacquiring"
      )));
    };
    syncFlashing();
    return amlogicManager.subscribe(syncFlashing);
  }, [amlogicManager]);

  const onConnect = useCallback(async () => {
    if (isAmlogicMode(mode)) return; // Amlogic connection is handled by the device table
    setBusy(true);
    setError("");
    const service = services[mode];
    const started = operationLogger.start(mode.toUpperCase() as "ADB" | "FASTBOOT", "connect", "Đang mở thiết bị USB");
    try {
      await service.connect();
      const deviceInfo = await service.getDeviceInfo();
      setInfo(deviceInfo);
      setConnectedMode(mode);
      operationLogger.success(deviceInfo.protocol, "connect", started, "Interface USB đã được claim");
    } catch (cause) {
      const message = userMessage(cause);
      setError(message);
      operationLogger.failure(mode.toUpperCase() as "ADB" | "FASTBOOT", "connect", started, message);
    } finally {
      setBusy(false);
    }
  }, [mode, services]);

  const onDisconnect = useCallback(async () => {
    if (!connectedMode) return;
    setBusy(true);
    try {
      await services[connectedMode].disconnect();
    } finally {
      setConnectedMode(undefined);
      setInfo(undefined);
      setBusy(false);
    }
  }, [connectedMode, services]);

  const switchMode = async (next: Mode) => {
    if (next === mode) return;
    setError("");
    if (connectedMode === "adb" || connectedMode === "fastboot") {
      setBusy(true);
      await services[connectedMode].disconnect().catch((cause) => setError(userMessage(cause)));
      setConnectedMode(undefined);
      setInfo(undefined);
      setBusy(false);
    }
    // Amlogic sessions persist across tab switches
    setMode(next);
  };

  const autoConnectAdb = useCallback(async (device: USBDevice) => {
    if (mode !== "adb" || connectedMode || busy || autoConnectInFlight.current) return;
    if (!matchesProtocolDevice(device, "ADB") || autoConnectAttempts.current.has(device)) return;

    autoConnectAttempts.current.add(device);
    autoConnectInFlight.current = true;
    setBusy(true);
    setError("");
    const started = operationLogger.start("ADB", "auto-connect", "Đang tự động mở thiết bị ADB");
    try {
      await services.adb.connectAuthorizedDevice(device);
      const deviceInfo = await services.adb.getDeviceInfo();
      setInfo(deviceInfo);
      setConnectedMode("adb");
      operationLogger.success("ADB", "auto-connect", started, "Đã tự động kết nối thiết bị ADB khi USB xuất hiện");
    } catch (cause) {
      const message = userMessage(cause);
      setError(message);
      operationLogger.failure("ADB", "auto-connect", started, message);
    } finally {
      autoConnectInFlight.current = false;
      setBusy(false);
    }
  }, [busy, connectedMode, mode, services]);

  const autoConnectFastboot = useCallback(async (device: USBDevice) => {
    if (mode !== "fastboot" || connectedMode || busy || autoConnectInFlight.current) return;
    if (!matchesProtocolDevice(device, "FASTBOOT") || autoConnectAttempts.current.has(device)) return;

    autoConnectAttempts.current.add(device);
    autoConnectInFlight.current = true;
    setBusy(true);
    setError("");
    const started = operationLogger.start("FASTBOOT", "auto-connect", "Đang tự động mở thiết bị Fastboot");
    try {
      await services.fastboot.connectAuthorizedDevice(device);
      const deviceInfo = await services.fastboot.getDeviceInfo();
      setInfo(deviceInfo);
      setConnectedMode("fastboot");
      operationLogger.success("FASTBOOT", "auto-connect", started, "Đã tự động kết nối thiết bị Fastboot khi USB xuất hiện");
    } catch (cause) {
      const message = userMessage(cause);
      setError(message);
      operationLogger.failure("FASTBOOT", "auto-connect", started, message);
    } finally {
      autoConnectInFlight.current = false;
      setBusy(false);
    }
  }, [busy, connectedMode, mode, services]);

  const autoConnectAdbOrFastboot = useCallback((device: USBDevice) => {
    if (mode === "adb") return autoConnectAdb(device);
    return autoConnectFastboot(device);
  }, [autoConnectAdb, autoConnectFastboot, mode]);

  useEffect(() => {
    const usb = navigator.usb;
    if (!usb) return;

    let disposed = false;
    let scanInFlight = false;
    const handleDisconnect = (event: USBConnectionEvent) => {
      // Amlogic sessions manage their own disconnect lifecycle.
      amlogicManager.handleUsbDisconnect(event.device);

      if (connectedMode !== "adb" && connectedMode !== "fastboot") return;
      const raw = connectedMode === "adb" ? services.adb.adapter.rawDevice : services.fastboot.transport.usbDevice;
      autoConnectAttempts.current.delete(event.device);
      if (raw && raw === event.device) {
        void services[connectedMode].disconnect();
        setConnectedMode(undefined);
        setInfo(undefined);
        setError("Thiết bị đã ngắt kết nối giữa thao tác. Không có thông báo thành công nào được phát ra.");
        operationLogger.info(connectedMode.toUpperCase() as "ADB" | "FASTBOOT", "disconnect", "Thiết bị bị ngắt kết nối");
      }
    };
    const handleConnect = (event: USBConnectionEvent) => {
      void amlogicManager.handleUsbConnect(event.device);
      if (!disposed) void autoConnectAdbOrFastboot(event.device);
    };
    const scanAuthorizedDevices = async () => {
      if (disposed || scanInFlight) return;
      scanInFlight = true;
      try {
        void amlogicManager.scanAuthorizedDevices();

        const devices = await usb.getDevices();
        for (const attempted of autoConnectAttempts.current) {
          if (!devices.some((device) => sameUsbIdentity(device, attempted))) autoConnectAttempts.current.delete(attempted);
        }
        if (disposed || busy) return;

        if (connectedMode === "adb" || connectedMode === "fastboot") {
          const currentDevice = connectedMode === "adb"
            ? services.adb.adapter.rawDevice
            : services.fastboot.transport.usbDevice;
          if (currentDevice && !devices.some((device) => sameUsbIdentity(device, currentDevice))) {
            autoConnectAttempts.current.delete(currentDevice);
            await services[connectedMode].disconnect();
            if (!disposed) {
              setConnectedMode(undefined);
              setInfo(undefined);
              operationLogger.info(connectedMode.toUpperCase() as "ADB" | "FASTBOOT", "disconnect", "Thiết bị không còn trong danh sách USB; trạng thái đã được làm mới");
            }
          }
          return;
        }
        if (connectedMode) return;

        if (mode === "adb" || mode === "fastboot") {
          const protocol = mode === "adb" ? "ADB" : "FASTBOOT";
          const device = devices.find((candidate) => matchesProtocolDevice(candidate, protocol));
          if (device) void autoConnectAdbOrFastboot(device);
        }
      } catch {
        // USB permission/device enumeration can fail transiently while a
        // device is re-enumerating. The next scan or connect event retries it.
      } finally {
        scanInFlight = false;
      }
    };
    usb.addEventListener("connect", handleConnect);
    usb.addEventListener("disconnect", handleDisconnect);
    void scanAuthorizedDevices();
    const scanTimer = window.setInterval(() => void scanAuthorizedDevices(), 1000);
    return () => {
      disposed = true;
      window.clearInterval(scanTimer);
      usb.removeEventListener("connect", handleConnect);
      usb.removeEventListener("disconnect", handleDisconnect);
    };
  }, [amlogicManager, autoConnectAdbOrFastboot, busy, connectedMode, mode, services]);

  const secure = window.isSecureContext || ["localhost", "127.0.0.1"].includes(location.hostname);
  const connected = (mode === "adb" || mode === "fastboot") && connectedMode === mode;
  const downloadDiagnostic = () => {
    const report = {
      appVersion: "0.1.0",
      userAgent: navigator.userAgent,
      secureContext: secure,
      webUsb: Boolean(navigator.usb),
      protocol: mode,
      descriptor: info ? { ...info, serial: "[ẩn theo tùy chọn mặc định]" } : undefined,
      log: operationLogger.snapshot,
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(report, null, 2)], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "usb-workbench-diagnostic.json";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const tabs: readonly [Mode, string][] = [
    ["amlogic-v3", "Amlogic V3"],
    ["amlogic-v2", "Amlogic V2"],
    ["adb", "ADB"],
    ["fastboot", "Fastboot"],
  ];

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-mark">
          <span>U</span>
          <div><strong>USB Device Workbench</strong><small>ADB · Fastboot · Amlogic</small></div>
        </div>
        <BrowserCompatibility onWebUsb={Boolean(navigator.usb)} secure={secure} />
      </header>
      <main>
        {error && <div className="error-banner" role="alert"><strong>Không hoàn thành thao tác</strong><span>{error}</span><button className="ghost" onClick={() => setError("")}>Đóng</button></div>}
        <nav className="tabs" aria-label="Chế độ giao thức">
          {tabs.map(([value, label]) => <button className={mode === value ? "tab active" : "tab"} key={value} aria-current={mode === value ? "page" : undefined} onClick={() => void switchMode(value)} disabled={busy}><span>{label}</span></button>)}
        </nav>
        {!isAmlogicMode(mode) && <DeviceConnectionCard protocolName={mode === "adb" ? "ADB device" : "Fastboot device"} info={info} connected={connected} busy={busy} autoConnect onConnect={() => void onConnect()} onDisconnect={() => void onDisconnect()} />}
        {mode === "adb" && <AdbPage service={services.adb} connected={connected} />}
        {mode === "fastboot" && <FastbootPage service={services.fastboot} connected={connected} />}
        {isAmlogicMode(mode) && <AmlogicPage manager={amlogicManager} generation={mode === "amlogic-v3" ? "adnl-v3" : "legacy-v2"} />}
        <OperationLog />
      </main>
      <footer>
        <div className="footer-meta"><span>Chạy trên Chrome/Edge desktop · HTTPS hoặc localhost</span><span className="footer-presence" aria-live="polite"><span className="footer-presence-dot" aria-hidden="true" />{presence ? `Đang truy cập ${presence.viewers} · đang flash ${presence.flashing} · tổng truy cập ${presence.totalVisits} · tổng flash ${presence.totalFlashes}` : "Đang truy cập …"}</span></div>
        <div className="footer-actions">
          <a className="footer-guide-link" href="/driver-windows" onClick={(event) => { event.preventDefault(); onOpenDriverGuide(); }}>Hướng dẫn cài driver Windows</a>
          <button className="secondary" onClick={downloadDiagnostic}>Xuất diagnostic</button>
          <button className="ghost" onClick={() => void onLogout()}>Đăng xuất</button>
        </div>
      </footer>
    </div>
  );
}
